import {
  useLayoutEffect,
  useRef,
} from 'react'


// Returns everything a `<dialog>` needs to stay modal for as long as it's
// mounted: a ref, the `closedby` attribute, and `cancel` and `close` handlers.
// The browser only traps focus, makes the rest of the page inert, and paints
// the backdrop when the element is opened with `showModal()`. Rendering the
// `open` attribute instead gives us a non-modal dialog with none of that, so
// the element has to be opened imperatively.
//
// Attach the returned ref to a `<dialog>` the calling component renders
// unconditionally. The effect dereferences the ref without a guard because
// React populates refs before effects run, so a null here would mean the
// element was never rendered at all.
//
// Pass a `handleCancel` for a dialog the visitor may dismiss. Leaving it out
// says the dialog must stay open, and the handlers below enforce that.
//
export default function useModalDialog({ handleCancel }) {
  const dialogRef = useRef(null)

  useLayoutEffect(() => {
    const dialog = dialogRef.current

    dialog.showModal()

    // Closing on the way out drops the element from the browser's top layer.
    return () => {
      dialog.close()
    }
  }, [])

  // Escape reaches the dialog as a close request, and setting `closedby` is the
  // only reliable way to refuse one. `preventDefault()` on `cancel` cannot do
  // it: the event is only cancelable while the document holds history-action
  // activation, and preventing one close request spends that activation. So a
  // dialog opened without a fresh interaction behind it closes on the first
  // Escape, and any dialog closes on the second.
  const closedby = ( handleCancel ) ? 'closerequest' : 'none'

  // `cancel` fires before the browser closes the dialog. Dismissing here is
  // what unmounts the dialog, and the close itself is the outcome we want, so
  // nothing is prevented.
  const onCancel = () => {
    if ( !handleCancel ) {
      return
    }

    handleCancel()
  }

  // Browsers without `closedby` support close the dialog anyway. Safari has
  // never shipped the attribute and Chrome only did so in 134, and `cancel`
  // cannot refuse the close there for the reason above, so reopening is all
  // that's left for a dialog that must not be dismissed. The visitor sees a
  // flicker instead of getting past the dialog.
  //
  // Once Safari ships `closedby`, this handler comes out and the `closedby`
  // attribute above is the whole implementation.
  const onClose = () => {
    if ( handleCancel ) {
      return
    }

    const dialog = dialogRef.current

    // The cleanup above closes the dialog when the component unmounts, and
    // `close()` queues this event for after React has cleared the ref. There's
    // no element left to reopen by then.
    if ( !dialog ) {
      return
    }

    dialog.showModal()
  }

  return {
    closedby,
    dialogRef,
    onCancel,
    onClose,
  }
}
